import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as store from "source/framework/customStore";
import { SaveDataObj } from "source/framework/saveData";
import * as riskService from "source/models/services/riskService";
import * as storeActions from "source/models/store/storeActions";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { IRiskStakeholderEditItem } from "source/models/riskStakeholder/riskStakeholderEditItem";
import { useEffect, useRef, useState } from "react";

interface IRiskStakeholderDialogProp {
    isReadOnly?: boolean;
    id: string;
    companyId: string;
    parentId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: (id: string) => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (id: string) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

interface IRiskStakeholderDialogState {
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    sortOrder: string;
    activeState: number;
        
    item: IRiskStakeholderEditItem;
}

export const RiskStakeholderDialog = (props: IRiskStakeholderDialogProp) => {
    const orgStateHash = useRef<string>("");
    const [state, setState] = useState<IRiskStakeholderDialogState>({
        item: null,
        id: "",
        rowId: "",
        isNew: false,
        code: "",
        comment: "",
        sortOrder: "0",
        activeState: 0
    });

    const loadData = async (id: string) => {
        const result = await AppUtils.callService(() => riskService.getRiskStakeholderEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.item;
        const stateChange = {
            item,
            id: item.id,
            rowId: item.rowId,
            isNew: item.isNew(),
            code: item.code,
            comment: item.comment,
            sortOrder: item.sortOrder.toString(10),
            activeState: item.activeState
        };
        setState(old => {
            const newState = { ...old, ...stateChange };
            orgStateHash.current = getStateHash(newState);
            return newState;
        });
    };

    useEffect(() => {
        // Initial load
        if (!state.item) {
            loadData(props.id);
            return;
        }
        // Update
        const safeLoadData = async () => {
            const cancelResult = await AppUtils.cancel(getStateHash(state), orgStateHash.current, null);
            if (cancelResult === ConfirmationDialogResult.Cancel) return;
            if (cancelResult === ConfirmationDialogResult.Yes) {
                if (!await saveToDb()) return;
                props.onRefreshList();
            }
            loadData(props.id);
        };
        
        safeLoadData();
    }, [props.companyId, props.id]);

    const handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            setState((old) => ({ ...old, code: value }));
        } else if (name === "comment") {
            setState((old) => ({ ...old, comment: value }));
        } else if (name === "sortOrder") {
            setState((old) => ({ ...old, sortOrder: value }));
        }
    };

    const checkErrors = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    const getSaveDataFromState = (state: IRiskStakeholderDialogState, forHash: boolean): SaveDataObj => {
        const result = new SaveDataObj();
        //Props cannot change in dialog so they are not needed for hash calculation
        result.append("companyId", forHash || !props.companyId ? null : props.companyId);
        result.append("parentId", forHash || !props.parentId ? null : props.parentId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("sortOrder", state.sortOrder.toInteger());
        result.append("activeState", state.activeState);
        return result;
    };

    const getStateHash = (aState: IRiskStakeholderDialogState): string => {
        return getSaveDataFromState(aState, true).hash;
    };

    const saveToDb = async (): Promise<string> => {
        if (!await AppUtils.validate(checkErrors)) return null;
        //Save to server
        const saveData = getSaveDataFromState(state, false);
        const result = await AppUtils.callService(() => riskService.saveRiskStakeholderEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        orgStateHash.current = getStateHash({ ...state, ...stateChange });
        setState((old) => ({ ...old, ...stateChange }));
        //Return new list item
        return result.id;
    };

    const handleSave = async (closeDialog: boolean) => {
        const savedId = await saveToDb();
        if (!savedId) return;
        if (!closeDialog) {
            props.onSaved(savedId);
        }
        if (!closeDialog) {
            loadData(savedId);
            return;
        }
        props.onClose(true);
    };

    const handleRemove = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRiskStakeholderParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove(state.id);
            if (!result) return;
            props.onClose(true);
        }
    };

    const handleCancel = async () => {
        const cancelResult = await AppUtils.cancel(getStateHash(state), orgStateHash.current, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    const canMoveOut = async (): Promise<boolean> => {
        return await AppUtils.canMoveOut(getStateHash(state), orgStateHash.current,
            async () => {
                const result = await saveToDb();
                if (!result) return false;
                return true;
            },
            props.onRefreshList,
            (newStateHash) => { orgStateHash.current = newStateHash; }
        );
    };

    const handlePrevious = async () => {
        if (!await canMoveOut()) return;
        props.onPrevious();
    };

    const handleNext = async () => {
        if (!await canMoveOut()) return;
        props.onNext();
    };

    if (!state.item) return null;

    const isNew = state.isNew;
    return (
        <Dialog
            id="riskStakeholderDialog"
            maxWidth="sm"
            title={isNew ? Translations.AddRiskStakeholder : (Translations.RiskStakeholder + ": " + state.item.code)}
            body={
                <Box display="flex" flexDirection="column">
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Name} fullWidth required margin="0">
                                <TextField name="code" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.code} required={true} onChange={handleChange} size="small" fullWidth disabled={props.isReadOnly} />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Description} fullWidth>
                                <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} value={state.comment} onChange={handleChange} multiline rows={5} size="small" fullWidth disabled={props.isReadOnly} />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title={Translations.SortOrder} fullWidth>
                                <TextField name="sortOrder" title={Translations.SortOrder} type="number" inputProps={{ maxLength: 5 }} value={state.sortOrder} onChange={handleChange} size="small" fullWidth disabled={props.isReadOnly} InputProps={{ inputProps: { min: 1, max: 100 } }} />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={<span>&nbsp;</span>} fullWidth>
                                <FormCheckBox
                                    title={Translations.InUse}
                                    checked={state.activeState > 0}
                                    disabled={props.isReadOnly}
                                    onChange={(checked: boolean) => {
                                        setState((old) => ({ ...old, activeState: checked ? 1 : 0 }));
                                    }}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                </Box>
            }
            actions={
                <Box display="flex" justifyContent="space-between" width="100%">
                    <DialogItemsNav
                        modifyUser={!isNew ? state.item.modifyUser : null}
                        hasPrevious={props.hasPrevious}
                        hasNext={props.hasNext}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                    />
                    <DialogButtons
                        isNew={isNew}
                        removeDisabled={props.isReadOnly}
                        saveDisabled={props.isReadOnly}
                        onCancel={handleCancel}
                        onRemove={!isNew ? handleRemove : null}
                        onSave={() => handleSave(false)}
                    />
                </Box>
            }
            onClose={handleCancel}
        />
    );
};